import { Box, Button, Link, Mark, Spinner, Text, VStack } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReactNode, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ControlledImagePicker } from "../../../../common/ImagePicker/ControlledImagePicker";
import { SecondFormSchema } from "./SecondFormStep.schema";
import { Stepper } from "../../../../common/Stepper/Stepper";
import { Header } from "../../../FormBuilder/FormBuilder.styles";
import {
  REQUIREMENTS_TO_REVIEW_LINK,
  REQUIREMENTS_TO_SCREENSHOT,
  REVIEW_TEMPLATES_LINK,
} from "../../ReviewForm.constants";
import { RequirementsList, RequirementsItem } from "../../ReviewForm.styles";
import { ControlledInput } from "../../../../common/CommonInput/ControlledInput";

interface SecondFormStepProps {
  onNext: (data: any) => void;
  onChange: (data: any) => void;
  header: string;
  description: ReactNode;
  onBack: () => void;
  defaultValues: any;
}

export const SecondFormStep = ({
  onNext,
  onBack,
  onChange,
  defaultValues,
  header,
}: SecondFormStepProps) => {
  const bankId = Number(localStorage.getItem("bankId"));
  const isBankiRu = bankId === 2;

  const requirementList = isBankiRu ? REQUIREMENTS_TO_REVIEW_LINK : REQUIREMENTS_TO_SCREENSHOT;

  const form = useForm({
    resolver: zodResolver(SecondFormSchema),
    mode: "onChange",
  });

  const {
    getValues,
    watch,
    formState: { isSubmitting },
  } = form;

  const [isValid, setValid] = useState(false);

  const variant = isValid ? "primary" : "secondary";

  const handleBack = () => {
    onChange(getValues());
    onBack();
  };

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues]);

  // Принудительная валидация формы.
  useEffect(() => {
    try {
      SecondFormSchema.parse(getValues());
      setValid(true);
    } catch (e) {
      setValid(false);
    }
  }, [watch()]);

  useEffect(() => {
    const reviewLink = getValues("reviewLink");
    if (
      reviewLink &&
      reviewLink.startsWith("https://www.banki.ru/services/responses/bank/response/") &&
      reviewLink.length <= 100
    ) {
      setValid(true);
    } else {
      setValid(false);
    }
  }, [watch("reviewLink")]);

  return (
    <FormProvider {...form}>
      <Stepper activeStepIndex={1} onBack={handleBack} steps={3} />
      <VStack gap="12px" w="full" alignItems="start">
        <Header noOfLines={3}>{isBankiRu ? "Добавьте ссылку на отзыв Банки.ру" : header}</Header>
        <Box>
          <Box fontSize="14px" color="text.heading" mb="16px">
            {isBankiRu ? (
              <>
                <Text color="accent.red" fontWeight={600} mb="16px">
                  Скопируйте ссылку на ваш отзыв из адресной строки браузера и вставьте её ниже.{" "}
                </Text>
                <Text>Если вы уже закрыли страницу с отзывом, найти его можно так:</Text>
              </>
            ) : (
              <>
                <Mark color="accent.red">Внимание!</Mark> На скриншоте должны быть видны следующие
                данные:
              </>
            )}
          </Box>
          <RequirementsList>
            {requirementList.map((requirement) => (
              <RequirementsItem key={requirement} fontSize="12px">
                {requirement}
              </RequirementsItem>
            ))}
          </RequirementsList>
        </Box>
      </VStack>
      {!isBankiRu && <ControlledImagePicker name="screenshot" />}
      <VStack gap="10px" w="full">
        {!isBankiRu && (
          <Link w="full" href={REVIEW_TEMPLATES_LINK} variant="outlined" isExternal>
            <Button variant="external" justifyContent="center">
              Пример отзыва
            </Button>
          </Link>
        )}
        {isBankiRu && (
          <ControlledInput name="reviewLink" placeholder="Вставьте ссылку в это поле" isRequired />
        )}
        <Button
          onClick={form.handleSubmit(onNext)}
          opacity={isSubmitting || !isValid ? 0.5 : 1}
          variant={variant}
          gap="10px"
        >
          Далее
          {isSubmitting ? <Spinner size={"sm"} color="white" /> : null}
        </Button>
        <Button onClick={handleBack} variant="secondary">
          Назад
        </Button>
      </VStack>
    </FormProvider>
  );
};
